/*----------------------------------
- COMPONENT: PUBLIC TABLE HOVER CARD
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr  ADR-0028
 *
 * The evidence behind one cell, as the marketing `HoverCard` (`.wp-hc`): the resolver's multi-line hover (a
 * title, its rows, its note), or a one-line card (a label and one sentence: an empty price's reason, the rating's
 * band, liquidity), else the bare cell.
 *
 * Decision (batch 7 part 3, the mock's "Deviations from shipped"): the card is built when a pointer rests on the
 * cell or the keyboard focuses it, no longer written into the HTML of every cell and revealed by CSS. The shipped
 * cards were 20 rows times up to 11 templates in the server HTML of `/domains` and of Home. The cell values stay in
 * the HTML and in the Markdown mirror (`buildDomainsLandingMarkdown.ts`, which never carried the hover lines); the
 * hover lines themselves (quoting registrar, similar-sales list, dictionary senses) leave the crawlable HTML, a GEO
 * trade the integrator reports to Gaetan. A phone shows no hover card (marketing.css), as product-design.md asks.
 *
 * @rule A cell with a card is a tab stop (the `HoverCard` default), so the keyboard and a screen reader reach every
 *       piece of evidence the pointer does (WCAG 2.1.1): as the shipped table (C0 put `tabIndex={0}` on each cell with
 *       a hover) and the mock (`tabindex="0"` on each cell with evidence). Gaetan, 2026-10-28 (review M1, answer A),
 *       replacing part 3 decision 86, whose premise ("the shipped cells had no tab stop") was false. A cell without a
 *       card takes no stop; the two faded rows take none (`inert`, `PublicDomainsTable`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { HoverCard } from '@uniquedomains/components/marketing/hover-card/HoverCard';

// Types
import type { ComponentChildren } from 'preact';
import type { TableCellHover } from '@uniquedomains/components/domains/table/cellValues';

/*----------------------------------
- TYPES
----------------------------------*/

type PublicTableHoverCardProps = {
    /** The cell's content, which the pointer rests on. */
    trigger: ComponentChildren;
    /** The resolver's card, when the cell has one. */
    hover?: TableCellHover | null;
    /** A one-line card: its label and its sentence, used when the cell has no multi-line card. */
    line?: { label: string; text: string } | null;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicTableHoverCard = ({ trigger, hover = null, line = null }: PublicTableHoverCardProps) => {
    if (hover) {
        return (
            <HoverCard
                trigger={trigger}
                label={hover.title}
                rows={hover.lines.map((entry) => ({ label: entry.key, value: entry.value }))}
                // `dm-pre` keeps the line breaks a word profile's dictionary senses carry (the mock's note).
                body={hover.note ? <p class="dm-pre">{hover.note}</p> : null}
            />
        );
    }
    if (line && line.text) return <HoverCard trigger={trigger} label={line.label} text={[line.text]} />;
    return <>{trigger}</>;
};

export default PublicTableHoverCard;
